/**
 * Change a voiceover's voice: correct the transcript sentence by sentence, pick a built-in voice,
 * preview, generate, then swap the new recording in on the timeline (one undo step).
 */

import type { Asset } from "@studio/engine";
import { Loader2, Play, X } from "lucide-react";
import { useEffect, useState } from "react";
import { api, fileUrl, type Job, type VoiceSegment } from "../api";
import { flush } from "./save";
import { useEditor } from "./store";

async function runJob(start: Promise<Job>, onProgress: (j: Job) => void): Promise<Job> {
  let job = await start;
  while (["queued", "running"].includes(job.status)) {
    onProgress(job);
    await new Promise((r) => setTimeout(r, 700));
    job = await api.job(job.id);
  }
  if (job.status !== "done") throw new Error(job.message || "failed");
  return job;
}

const stamp = (t: number) => `${Math.floor(t / 60)}:${(t % 60).toFixed(1).padStart(4, "0")}`;

export function VoiceDialog({ assetId }: { assetId: string }) {
  const project = useEditor((s) => s.project)!;
  const src = project.assets.find((a) => a.id === assetId);
  const [segs, setSegs] = useState<VoiceSegment[] | null>(null);
  const [voices, setVoices] = useState<{ id: string; label: string }[]>([]);
  const [voice, setVoice] = useState(() => localStorage.getItem("studio:voice") ?? "am_michael");
  const [speed, setSpeed] = useState(1);
  const [keepTiming, setKeepTiming] = useState(true);
  const [busy, setBusy] = useState<{ what: string; job?: Job } | null>({ what: "Reading the words" });
  const [preview, setPreview] = useState<string | null>(null);
  const [result, setResult] = useState<Asset | null>(null);
  const [done, setDone] = useState("");
  const [err, setErr] = useState("");
  const close = () => useEditor.getState().set({ panel: null });

  useEffect(() => {
    api.voices().then(setVoices).catch(() => {});
    runJob(api.voice(project.id, assetId, { mode: "script" }), (job) => setBusy({ what: "Reading the words", job }))
      .then((j) => setSegs((j.output?.segments as VoiceSegment[]) ?? []))
      .catch((e) => setErr(e.message))
      .finally(() => setBusy(null));
  }, [project.id, assetId]);

  const pick = (v: string) => {
    setVoice(v);
    try {
      localStorage.setItem("studio:voice", v);
    } catch {}
  };

  const run = async (mode: "preview" | "speak") => {
    if (!segs) return;
    setErr("");
    setDone("");
    const what = mode === "preview" ? "Speaking the first sentence" : "Speaking";
    setBusy({ what });
    try {
      await flush();
      const j = await runJob(api.voice(project.id, assetId, { mode, voice, speed, keepTiming, segments: segs }), (job) => setBusy({ what, job }));
      if (mode === "preview") setPreview(`${fileUrl(project.id, j.output!.file!)}?v=${j.id}`);
      else setResult(j.output!.asset as Asset);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const use = (replace: boolean) => {
    if (!result) return;
    const n = project.audio.filter((c) => c.assetId === assetId).length;
    useEditor.getState().edit((d) => {
      if (!d.assets.some((a) => a.id === result.id)) d.assets.push(result);
      if (!replace) return;
      for (const c of d.audio) {
        if (c.assetId !== assetId) continue;
        c.assetId = result.id;
        if (!keepTiming) {
          c.trimIn = 0;
          c.trimOut = null;
        }
      }
    });
    window.dispatchEvent(new Event("studio:poll"));
    setDone(replace ? `Replaced on the timeline (${n} clip${n === 1 ? "" : "s"}). Undo puts the original back.` : "Added to the project's audio.");
  };

  const edit = (i: number, text: string) => setSegs((s) => s!.map((x, j) => (j === i ? { ...x, text } : x)));
  const hasCues = project.subtitles.length > 0;

  return (
    <div className="modal-back" onPointerDown={close}>
      <div className="modal voice" onPointerDown={(e) => e.stopPropagation()}>
        <header>
          <h3>Change voice: {src?.name}</h3>
          <button className="icon" onClick={close}><X size={18} /></button>
        </header>
        <p className="muted small">
          The words below come from the recording; fix anything misheard before generating. Each sentence is spoken with the voice you pick, on this server.
        </p>
        <div className="voice-controls">
          <label>Voice
            <select value={voice} onChange={(e) => pick(e.target.value)}>
              {voices.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
          </label>
          <label>Speed
            <input type="number" className="num short" min={0.6} max={1.6} step={0.05} value={speed} onChange={(e) => setSpeed(+e.target.value || 1)} />
          </label>
          <label className="check" title="Each sentence starts where it did in the original, so scenes timed to this clip still line up">
            <input type="checkbox" checked={keepTiming} onChange={(e) => setKeepTiming(e.target.checked)} /> Keep the original timing
          </label>
          <span className="grow" />
          <button disabled={!!busy || !segs?.length} onClick={() => run("preview")}><Play size={14} /> Preview first sentence</button>
          <button className="primary" disabled={!!busy || !segs?.length} onClick={() => run("speak")}>Generate</button>
        </div>
        {busy && (
          <p className="muted small"><Loader2 size={13} className="spin" /> {busy.job?.message && busy.job.message !== "starting" ? busy.job.message : busy.what}
            {busy.job?.status === "queued" ? " (waiting for the worker)" : busy.job ? ` ${Math.round(busy.job.progress * 100)}%` : "…"}</p>
        )}
        {err && <p className="bad small">{err}</p>}
        <div className="voice-compare">
          {src && <label>Original <audio controls preload="none" src={fileUrl(project.id, src.file)} /></label>}
          {preview && <label>Preview <audio controls autoPlay src={preview} /></label>}
          {result && <label>New: {result.name} <audio controls src={fileUrl(project.id, result.file)} /></label>}
        </div>
        {result && (
          <div className="analysis-actions" style={{ justifyContent: "flex-start" }}>
            <button className="primary" onClick={() => use(true)} disabled={!!done}>Replace on the timeline</button>
            <button onClick={() => use(false)} disabled={!!done}>Just add to the project</button>
            {done && <span className="good small">{done}{hasCues ? " Regenerate captions to match the new words." : ""}</span>}
          </div>
        )}
        {segs && (
          <div className="import-table voice-script">
            <table>
              <thead><tr><th>At</th><th>Words</th></tr></thead>
              <tbody>
                {segs.map((s, i) => (
                  <tr key={i}>
                    <td className="muted">{stamp(s.start)}</td>
                    <td><textarea rows={2} value={s.text} onChange={(e) => edit(i, e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
